import { Injectable } from '@angular/core';
import { ContentGroupComponent } from '../storyblok/components/content-group/content-group.component';
import { HeroDefaultComponent } from '../storyblok/components/hero-default/hero-default.component';
import { StoryblokButtonComponent } from '../storyblok/components/storyblok-button/storyblok-button.component';
import { TeaserComponent } from '../storyblok/components/teaser/teaser.component';
import { TextComponent } from '../storyblok/components/text/text.component';
import { TitleComponent } from '../storyblok/components/title/title.component';

@Injectable({
  providedIn: 'root',
})
export class StoryblokLoadComponentsService {
  // Bloks on most marketing pages, plus the above-the-fold hero: bundled into main.js instead of lazy chunks.
  // Resolved here, not at module level: these components import this service, so in the ESM cycle the
  // class bindings may still be undefined while this module evaluates.
  components: { [key: string]: unknown } = {
    button: StoryblokButtonComponent,
    content_group: ContentGroupComponent,
    hero_default: HeroDefaultComponent,
    teaser: TeaserComponent,
    text: TextComponent,
    title: TitleComponent,
  };

  public async loadComponents(componentTypes: string[] | undefined) {
    if (!componentTypes?.length) return;
    await Promise.all(
      componentTypes.map(async type => {
        if (this.components[type]) return;
        if (type === 'badge_list') {
          const { BadgeListComponent } = await import('../storyblok/components/badge-list/badge-list.component');
          return (this.components[type] = BadgeListComponent);
        }
        if (type === 'breadcrumb') {
          const { BreadcrumbComponent } = await import('../storyblok/components/breadcrumb/breadcrumb.component');
          return (this.components[type] = BreadcrumbComponent);
        }
        if (type === 'blog_post_link_list') {
          const { BlogPostLinkListComponent } =
            await import('../storyblok/components/blog-post-link-list/blog-post-link-list.component');
          return (this.components[type] = BlogPostLinkListComponent);
        }
        if (type === 'latest_blog_posts') {
          const { LatestBlogPostsComponent } =
            await import('../storyblok/components/latest-blog-posts/latest-blog-posts.component');
          return (this.components[type] = LatestBlogPostsComponent);
        }
        if (type === 'blur') {
          const { StoryblokBlurComponent } =
            await import('../storyblok/components/storyblok-blur/storyblok-blur.component');
          return (this.components[type] = StoryblokBlurComponent);
        }
        if (type === 'button_list') {
          const { StoryblokButtonListComponent } =
            await import('../storyblok/components/storyblok-button-list/storyblok-button-list.component');
          return (this.components[type] = StoryblokButtonListComponent);
        }
        if (type === 'check_list') {
          const { CheckListComponent } = await import('../storyblok/components/check-list/check-list.component');
          return (this.components[type] = CheckListComponent);
        }
        if (type === 'claim') {
          const { ClaimComponent } = await import('../storyblok/components/claim/claim.component');
          return (this.components[type] = ClaimComponent);
        }
        if (type === 'company_logo_list') {
          const { CompanyLogoListComponent } =
            await import('../storyblok/components/company-logo-list/company-logo-list.component');
          return (this.components[type] = CompanyLogoListComponent);
        }
        if (type === 'comparison') {
          const { ComparisonComponent } = await import('../storyblok/components/comparison/comparison.component');
          return (this.components[type] = ComparisonComponent);
        }
        if (type === 'conversion') {
          const { ConvertComponent } = await import('../tools/convert/components/convert/convert.component');
          return (this.components[type] = ConvertComponent);
        }
        if (type === 'conversion_format_switcher') {
          const { ConversionFormatSwitcherComponent } =
            await import('../tools/convert/components/conversion-format-switcher/conversion-format-switcher.component');
          return (this.components[type] = ConversionFormatSwitcherComponent);
        }
        if (type === 'convert_from_format_list') {
          const { ConvertFromFormatListComponent } =
            await import('../tools/convert/components/convert-from-format-list/convert-from-format-list.component');
          return (this.components[type] = ConvertFromFormatListComponent);
        }
        if (type === 'convert_to_format_list') {
          const { ConvertToFormatListComponent } =
            await import('../tools/convert/components/convert-to-format-list/convert-to-format-list.component');
          return (this.components[type] = ConvertToFormatListComponent);
        }
        if (type === 'metadata_tool') {
          const { MetadataToolComponent } =
            await import('../tools/metadata/components/metadata-tool/metadata-tool.component');
          return (this.components[type] = MetadataToolComponent);
        }
        if (type === 'faq_list') {
          const { FaqListComponent } = await import('../storyblok/components/faq-list/faq-list.component');
          return (this.components[type] = FaqListComponent);
        }
        if (type === 'feature_item_list') {
          const { FeatureItemListComponent } =
            await import('../storyblok/components/feature-item-list/feature-item-list.component');
          return (this.components[type] = FeatureItemListComponent);
        }
        if (type === 'form') {
          const { FormComponent } = await import('../storyblok/components/form/form.component');
          return (this.components[type] = FormComponent);
        }
        if (type === 'gallery_showcase_list') {
          const { GalleryShowcaseListComponent } =
            await import('../storyblok/components/gallery-showcase-list/gallery-showcase-list.component');
          return (this.components[type] = GalleryShowcaseListComponent);
        }
        if (type === 'hero_perspective_themes') {
          const { HeroPerspectiveThemesComponent } =
            await import('../storyblok/components/hero-perspective-themes/hero-perspective-themes.component');
          return (this.components[type] = HeroPerspectiveThemesComponent);
        }
        if (type === 'hero_tiles_floating') {
          const { HeroTilesFloatingComponent } =
            await import('../storyblok/components/hero-tiles-floating/hero-tiles-floating.component');
          return (this.components[type] = HeroTilesFloatingComponent);
        }
        if (type === 'hero_integrations') {
          const { HeroIntegrationsComponent } =
            await import('../storyblok/components/hero-integrations/hero-integrations.component');
          return (this.components[type] = HeroIntegrationsComponent);
        }
        if (type === 'hero_pricing') {
          const { HeroPricingComponent } = await import('../storyblok/components/hero-pricing/hero-pricing.component');
          return (this.components[type] = HeroPricingComponent);
        }
        if (type === 'integrations_list') {
          const { IntegrationsListComponent } =
            await import('../storyblok/components/integrations-list/integrations-list.component');
          return (this.components[type] = IntegrationsListComponent);
        }
        if (type === 'marquee_proofing') {
          const { MarqueeProofingComponent } =
            await import('../storyblok/components/marquee-proofing/marquee-proofing.component');
          return (this.components[type] = MarqueeProofingComponent);
        }
        if (type === 'showcase_teaser') {
          const { ShowcaseTeaserComponent } =
            await import('../storyblok/components/showcase-teaser/showcase-teaser.component');
          return (this.components[type] = ShowcaseTeaserComponent);
        }
        if (type === 'icon_with_text_list') {
          const { IconWithTextListComponent } =
            await import('../storyblok/components/icon-with-text-list/icon-with-text-list.component');
          return (this.components[type] = IconWithTextListComponent);
        }
        if (type === 'animation') {
          const { AnimationComponent } = await import('../storyblok/components/animation/animation.component');
          return (this.components[type] = AnimationComponent);
        }
        if (type === 'image') {
          const { ImageComponent } = await import('../storyblok/components/image/image.component');
          return (this.components[type] = ImageComponent);
        }
        if (type === 'point_list') {
          const { PointListComponent } = await import('../storyblok/components/point-list/point-list.component');
          return (this.components[type] = PointListComponent);
        }
        if (type === 'pinned_blog_post_link') {
          const { PinnedBlogPostLinkComponent } =
            await import('../storyblok/components/pinned-blog-post-link/pinned-blog-post-link.component');
          return (this.components[type] = PinnedBlogPostLinkComponent);
        }
        if (type === 'pricing_box_list') {
          const { PricingBoxListComponent } =
            await import('../storyblok/components/pricing-box-list/pricing-box-list.component');
          return (this.components[type] = PricingBoxListComponent);
        }
        if (type === 'product_comparison_list') {
          const { ProductComparisonListComponent } =
            await import('../storyblok/components/product-comparison-list/product-comparison-list.component');
          return (this.components[type] = ProductComparisonListComponent);
        }
        if (type === 'product_feature_list') {
          const { ProductFeatureListComponent } =
            await import('../storyblok/components/product-feature-list/product-feature-list.component');
          return (this.components[type] = ProductFeatureListComponent);
        }
        if (type === 'sub_menu') {
          const { SubMenuComponent } = await import('../storyblok/components/sub-menu/sub-menu.component');
          return (this.components[type] = SubMenuComponent);
        }
        if (type === 'table_of_contents') {
          const { TableOfContentsComponent } =
            await import('../storyblok/components/table-of-contents/table-of-contents.component');
          return (this.components[type] = TableOfContentsComponent);
        }
        if (type === 'video') {
          const { VideoComponent } = await import('../storyblok/components/video/video.component');
          return (this.components[type] = VideoComponent);
        }
        if (type === 'mini_comparison') {
          const { MiniComparisonComponent } =
            await import('../storyblok/components/mini-comparison/mini-comparison.component');
          return (this.components[type] = MiniComparisonComponent);
        }
        if (type === 'custom_rating_display') {
          const { CustomRatingDisplayComponent } =
            await import('../storyblok/components/custom-rating-display/custom-rating-display.component');
          return (this.components[type] = CustomRatingDisplayComponent);
        }
        if (type === 'rating_display') {
          const { RatingDisplayWrapperComponent } =
            await import('../storyblok/components/rating-display-wrapper/rating-display-wrapper.component');
          return (this.components[type] = RatingDisplayWrapperComponent);
        }
        if (type === 'blog_meta') {
          const { BlogMetaComponent } = await import('../storyblok/components/blog-meta/blog-meta.component');
          return (this.components[type] = BlogMetaComponent);
        }
        if (type === 'competitor_list') {
          const { CompetitorListComponent } =
            await import('../compare/components/competitor-list/competitor-list.component');
          return (this.components[type] = CompetitorListComponent);
        }
        if (type === 'competitor_review_list') {
          const { CompetitorReviewListComponent } =
            await import('../compare/components/competitor-review-list/competitor-review-list.component');
          return (this.components[type] = CompetitorReviewListComponent);
        }
        if (type === 'competitor_alternatives_list') {
          const { CompetitorAlternativesListComponent } =
            await import('../compare/components/competitor-alternatives-list/competitor-alternatives-list.component');
          return (this.components[type] = CompetitorAlternativesListComponent);
        }
        if (type === 'competitor_pricing_points') {
          const { CompetitorPricingPointsComponent } =
            await import('../compare/components/competitor-pricing-points/competitor-pricing-points.component');
          return (this.components[type] = CompetitorPricingPointsComponent);
        }
        if (type === 'competitor_comparison_table') {
          const { CompetitorComparisonTableComponent } =
            await import('../compare/components/competitor-comparison-table/competitor-comparison-table.component');
          return (this.components[type] = CompetitorComparisonTableComponent);
        }
        if (type === 'competitor_logo_pair') {
          const { CompetitorLogoPairComponent } =
            await import('../compare/components/competitor-logo-pair/competitor-logo-pair.component');
          return (this.components[type] = CompetitorLogoPairComponent);
        }
        if (type === 'carousel') {
          const { CarouselComponent } = await import('../storyblok/components/carousel/carousel.component');
          return (this.components[type] = CarouselComponent);
        }
        if (type === 'two_column') {
          const { TwoColumnComponent } = await import('../storyblok/components/two-column/two-column.component');
          return (this.components[type] = TwoColumnComponent);
        }
        if (type === 'review_list') {
          const { ReviewListComponent } = await import('../storyblok/components/review-list/review-list.component');
          return (this.components[type] = ReviewListComponent);
        }
        if (type === 'review_carousel') {
          const { ReviewCarouselComponent } =
            await import('../storyblok/components/review-carousel/review-carousel.component');
          return (this.components[type] = ReviewCarouselComponent);
        }
        if (type === 'table') {
          const { TableComponent } = await import('../storyblok/components/table/table.component');
          return (this.components[type] = TableComponent);
        }
        if (type === 'custom_html') {
          const { CustomHtmlComponent } = await import('../storyblok/components/custom-html/custom-html.component');
          return (this.components[type] = CustomHtmlComponent);
        }
        if (type === 'coupon') {
          const { CouponComponent } = await import('../storyblok/components/coupon/coupon.component');
          return (this.components[type] = CouponComponent);
        }
        if (type === 'toast') {
          const { StoryblokToastComponent } =
            await import('../storyblok/components/storyblok-toast/storyblok-toast.component');
          return (this.components[type] = StoryblokToastComponent);
        }
        if (type === 'updates') {
          const { UpdatesComponent } = await import('../storyblok/components/updates/updates.component');
          return (this.components[type] = UpdatesComponent);
        }
        if (type === 'page_list') {
          const { PageListComponent } = await import('../storyblok/components/page-list/page-list.component');
          return (this.components[type] = PageListComponent);
        }
      })
    );
  }
}
